import { ExceptionService } from '../services/exception.service';
import {
  Component,
  Input,
  Output,
  NgZone,
  EventEmitter,
  AfterContentChecked,
  ElementRef,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  UntypedFormBuilder,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import {
  TogetherAuctionDIPLoanDetails,
  TogetherAuctionDIPSecurityDetails,
  TogetherAuctionDIPSecurityRequest,
  TogetherBaseAddress,
} from './../models/api.models';

import { Router } from '@angular/router';

// Internal imports.
import { TogetherDIPStageContainer } from './../models/app.models';
import { NodeAPIService } from './../services/node.api.service';
import { AddressComponent } from 'app/components/address.component';
import { TogetherAuctionDIPEntityType } from './../models/enums';
import { NodeAPIEntityService } from './../services/node.entity.service';
import { DipStateService } from '../services/dip-state-service.service';

// Global imports.
import * as _ from 'lodash-es';
import moment from 'moment';

@Component({
    selector: 'together-auction-dip-property-details',
    templateUrl: './dip.property_details.component.html',
    styleUrls: ['dip.property_details.component.scss'],
    providers: [NodeAPIService, NodeAPIEntityService],
    standalone: false
})
export class TogetherAuctionDIPPropertyDetailsComponent implements OnInit {
  /**
   * Property details form.
   *
   * @type {FormGroup}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadPropertyDetailsForm: UntypedFormGroup;

  /**
   * Address control (incoming from address component)
   *
   * @type {FormGroup}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadAddress: UntypedFormGroup = new UntypedFormGroup({});

  /**
   * Property type control. DIP only.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadPropertyType: UntypedFormControl = new UntypedFormControl(null, []);

  /**
   * Bedrooms control. DIP only.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadPropertyNumberBedrooms: UntypedFormControl = new UntypedFormControl(null, [
    Validators.max(10),
  ]);

  /**
   * Auction house control.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadPropertyAuctionHouse: UntypedFormControl = new UntypedFormControl(null, []);

  /**
   * Year of auction.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadPropertyAuctionYear: UntypedFormControl = new UntypedFormControl(null, [
    Validators.min(new Date().getFullYear()),
  ]);

  /**
   * Month of auction.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadPropertyAuctionMonth: UntypedFormControl = new UntypedFormControl(null, [
    Validators.min(1),
    Validators.max(12),
  ]);

  /**
   * Day of auction.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  tadPropertyAuctionDay: UntypedFormControl = new UntypedFormControl(null, [
    Validators.min(1),
    Validators.max(31),
  ]);

  /**
   * Toggle for if the user has searched already.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  hasSearched: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
  ]);

  /**
   * Address for this component.
   * TODO: Remove from component scope.
   *
   * @type {TogetherBaseAddress}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  address: TogetherBaseAddress = new TogetherBaseAddress();

  /**
   * The address lookup formGroup.
   * TODO: Remove from component scope.
   *
   * @type {FormGroup}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  addresslookup: UntypedFormGroup = this.fb.group({});

  /**
   * The property.
   *
   * @type {TogetherAuctionDIPSecurityDetails}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  @Input() property: TogetherAuctionDIPSecurityDetails = new TogetherAuctionDIPSecurityDetails();

  /**
   * Incoming case ID.
   *
   * @type {string}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  @Input() incoming_case_id: string;

  /**
   * Stage.
   * TODO: Remove this and use state service.
   *
   * @type {TogetherDIPStageContainer}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  @Input() stage: TogetherDIPStageContainer;

  /**
   * Input from DIP component.
   * TODO: remove this from inputs and move to state service.
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  @Input() is_dip: boolean;

  /**
   * Output for stage changing.
   * TODO: Remove this from outputs and move to state service.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  @Output() stage_changing = new EventEmitter();

  /**
   * Is the page loading?
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  isLoading: boolean = false;

  /**
   *
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  isSubmitted: boolean = false;

  /**
   * HACK: This needs to be removed and correct input validation done via angular validators.
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  hasPostCodeNotEntered: boolean = false;

  /**
   * Is this DIP journey?
   * TODO: Remove from component scope and add solely to service.
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  isDipJourney: boolean;

  /**
   * Property Type
   * TODO: Remove and use service.
   *
   * @type {number}
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  propertyType: number;

  /**
   * JWT Token
   *
   * @type {string}
   */
  jwt_token: string;

  /**
   * Custom validator for hometrack Property Type
   *
   * @param {FormControl} control
   * @returns
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  propertyTypeValidator() {
    var self = this;
    return (input: UntypedFormControl): { [key: string]: any } => {

      // Get value as int - if it's "please select" it is null, so therefore 0.
      const doesApply =
        _.toInteger(input.value) === 0 &&
        self.propertyType === 0 &&
        self.isDipJourney;


      return doesApply ? {
        required: true,
      } : null;

    };
  }

  /**
   * Validates all fields.
   *
   * @param {string} dayControl
   * @param {string} monthControl
   * @param {string} yearControl
   * @returns
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  validateFields(dayControl: string, monthControl: string, yearControl: string) {
    return (group: UntypedFormGroup): { [key: string]: any } => {
      const dayctrl = group.controls[dayControl];
      const monthctrl = group.controls[monthControl];
      const yearctrl = group.controls[yearControl];
      const day = dayctrl.value;
      const month = monthctrl.value;
      const year = yearctrl.value;

      const eachValid = [
        _.isNil(day) === false,
        _.isNil(month) === false,
        _.isNil(year) === false,
      ];
      const valuesUpdated = _.includes(eachValid, true);
      const allValuesSet = eachValid.every(p => p === true);

      if (valuesUpdated && allValuesSet === false) {
        return {
          fillInAllFields: true,
        };
      }
      // If all fields are filled in
      if (allValuesSet) {

        // Is the date wrong
        const date = moment([year, month - 1, day]);
        const past = moment().subtract({ days: 27 });
        const future = moment().add({ months: 3 });
        const errors = {};
        // Is it a valid date?
        if (date.isValid() === false) {
          errors['invalidDate'] = true;
        }

        if (date.isAfter(future, 'day')) {
          errors['tooFarInFuture'] = true;
        }
        if (date.isBefore(past, 'day')) {
          errors['tooFarInPast'] = true;
        }
        if (_.isEmpty(errors) === false) {
          return errors;
        }
      }
      return null;
    };
  }

  /**
   * Used for hiding number of bedrooms.
   *
   * @readonly
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  get isCommercialCase() {
    return !this.state.isDip;
  }

  /**
   * Increment the number of bedrooms.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  incrementBedrooms(): void {
    if (this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').value < 11) {
      this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').setValue(
        _.toInteger(this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').value) + 1,
      );
      this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').updateValueAndValidity();
    }
  }

  /**
   * Decrement the number of bedrooms.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  decrementBedrooms(): void {
    if (this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').value > 0) {
      this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').setValue(
        _.toInteger(this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').value) - 1,
      );
      this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').updateValueAndValidity();
    }
  }

  /**
   * Handle the incoming form.
   *
   * @param {any} form
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  handleIncomingForm(form: any) {

    const currentAddress = this.tadPropertyDetailsForm.get('tadAddress');
    const hasSearched = this.tadPropertyDetailsForm.get('hasSearched').valid;

    // Don't waste time resetting and upsetting the viewmodel.
    if (form === currentAddress && hasSearched) {
      return;
    }

    let validators = [];

    if (form.invalid) {
      validators = [Validators.required];
    }

    // Clear any errors from the hasSearched, and set validators as required if not valid.
    this.tadPropertyDetailsForm.get('hasSearched').clearValidators();
    this.tadPropertyDetailsForm.get('hasSearched').setValidators(validators);

    // Instead of rebuilding form, re-add the address in the form.
    this.tadPropertyDetailsForm.removeControl('tadAddress');
    this.tadPropertyDetailsForm.addControl('tadAddress', form as UntypedFormControl);

    // Update the validity.
    this.tadPropertyDetailsForm.updateValueAndValidity();
  }


  /**
   * Changes stage when dip journey.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  changeStage() {
    // TS error, please don't correct to boolean or it'll break. Raised on GH already.
    const stage = this.isDipJourney ?
      this.stage.current_stage.forward_reference : 'tad_stage_additional_loan_details';

    this.stage_changing.emit(stage);
  }

  /**
   * Calls API to save property details.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  savePropertyDetails() {
    this.isLoading = true;
    if (_.isNil(this.property) === false) {
      this.property.caseSecurityRequest.CaseId = this.incoming_case_id;
      this.property.caseSecurityRequest.ValuationRequest[0].ValuationMethodId = '11';
      this.property.existingChargeDetailRequest = [];
      this.api.savePropertyDetails(this.incoming_case_id, null, this.property)
        .subscribe(
          (success) => {
            try {
              const obj = (success as any); //JSON.parse(success.text());
              this.property = new TogetherAuctionDIPSecurityDetails(success);
            } catch (jsonParseError) {
              return;
            }
            const output = {
              case_id: this.incoming_case_id
            };
            const base64 = btoa(JSON.stringify(output));
            this.entity.getJWT(base64).subscribe(
              (success) => {
                this.jwt_token = (success as any).jwt;
                this.router.navigate(['do_retrieve_jwt', this.jwt_token, 
                'tad_stage_dip_overview']);
              },
              (error) => {
                const errorInner = _.defaultTo(error.error, {});
                this.exception.haveReceivedError(errorInner);
              });
            this.isLoading = false;
          },
          (error) => {
            this.exception.haveReceivedError(error);
            this.isLoading = false;
          },
        );
    } else {
      this.isLoading = false;
    }
  }


  /**
   * Binds data to API object.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  bindAPIData() {
    // Address
    const address = this.tadPropertyDetailsForm.get('tadAddress') as UntypedFormGroup;

    // Check Address is initialised.
    if (_.isNil(address) === false && address.valid && _.isNil(this.property) === false) {

      // Bind auction date
      const day = this.tadPropertyDetailsForm.get('tadPropertyAuctionDay').value;
      const month = this.tadPropertyDetailsForm.get('tadPropertyAuctionMonth').value;
      const year = this.tadPropertyDetailsForm.get('tadPropertyAuctionYear').value;
      const date = new Date(year, month - 1, day);
      const addressInit = Object.keys(address.controls).length > 0;

      if (addressInit) {
        // Bind auction date.
        this.property.caseSecurityRequest.AuctionDate =
          _.isNil(day) || _.isNil(month) || _.isNil(year) ? null : date.toISOString();

        this.property.caseSecurityRequest.CaseSecurityAttributeRequest[0].PropertyTypeId =
          this.tadPropertyDetailsForm.get('tadPropertyType').value;

        // Bind address
        this.property.caseSecurityRequest.Address['CaseId'] = this.incoming_case_id;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.HouseName =
          address.get('addressHouseName').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.HouseNumber =
          address.get('addressHouseNumber').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.District =
          address.get('addressDistrict').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.County =
          address.get('addressCounty').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest['Street'] =
          address.get('addressStreet').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.Street1 =
          address.get('addressStreet').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.Street2 =
          address.get('addressStreet').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.PostTown =
          address.get('addressTownCity').value;
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest.Postcode =
          address.get('addressPostcode').value;
      }
      this.property.caseSecurityRequest.Address =
        this.property.caseSecurityRequest.SecurityRequest.AddressRequest;
      this.property.caseSecurityRequest.AuctionHouse =
        this.tadPropertyDetailsForm.get('tadPropertyAuctionHouse').value;
      this.property.caseSecurityRequest.CaseSecurityAttributeRequest[0].Bedroom =
        this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').value;
    }

  }

  /**
   * Handles binding of data on init.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  bindDataToForm() {
    if (_.isNil(this.property) === false) {
      const date = this.property.caseSecurityRequest.AuctionDate;

      if (_.isNil(date) === false) {
        const fullDate = new Date(date);
        this.tadPropertyDetailsForm.get('tadPropertyAuctionDay').setValue(
          fullDate.getDate(),
        );
        this.tadPropertyDetailsForm.get('tadPropertyAuctionMonth').setValue(
          fullDate.getMonth() + 1,
        );
        this.tadPropertyDetailsForm.get('tadPropertyAuctionYear').setValue(
          fullDate.getUTCFullYear(),
        );
      }

      if (_.isNil(this.property.caseSecurityRequest.Address) === false &&
        this.property.caseSecurityRequest.SecurityRequest.AddressId !== 0) {
        this.address = new TogetherBaseAddress(
          this.property.caseSecurityRequest.SecurityRequest.AddressRequest,
        );
      }

      this.tadPropertyDetailsForm.get('tadPropertyType').setValue(
        this.property.caseSecurityRequest.CaseSecurityAttributeRequest[0].PropertyTypeId,
      );
      this.tadPropertyDetailsForm.get('tadPropertyAuctionHouse').setValue(
        this.property.caseSecurityRequest.AuctionHouse,
      );
      this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').setValue(
        this.property.caseSecurityRequest.CaseSecurityAttributeRequest[0].Bedroom,
      );
      this.tadPropertyDetailsForm.get('hasSearched').setValue(true);
      this.tadPropertyDetailsForm.updateValueAndValidity();
    }
  }

  /**
   * Get data from API.
   *
   * @param {any} case_id
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  initData(caseId) {
    this.isLoading = true;
    this.entity.getAllEntities(TogetherAuctionDIPEntityType.SecurityDetails, caseId)
      .subscribe(
        (success) => {
          if (_.isArray(success)) {
            const properties = (success as any).map(
              resp => new TogetherAuctionDIPSecurityDetails(resp),
            );

            if (properties.length > 0) {
              this.property = properties[0];
            } else {
              this.property = new TogetherAuctionDIPSecurityDetails();
            }
            this.bindDataToForm();
          }
          this.isLoading = false;
        },
        (error) => {
          this.exception.haveReceivedError(error);
          this.isLoading = false;
        },
      );
  }

  /**
   * Inits the component.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  init() {
    // Is the entity not new? If so, let's get the details.
    if (this.entity.isNew(this.incoming_case_id) === false) {
      this.initData(this.incoming_case_id);
    } else {
      // If we're not allowed to have a new reference, move.
      if (this.entity.allowNew(this.stage.current_stage.reference) === false) {
        this.stage_changing.emit('tad_stage_info');
      }
    }
  }

  /**
   * Handles init of component.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  ngOnInit() {
    window.scrollTo(0, 0);
    this.init();
    this.rebuildFormForCommercialProperty();
  }

  /**
   * Handle foward step.
   *
   * @param {any} forward
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  onForwardClicked(forward: any) {
    this.isSubmitted = true;
    if (this.tadPropertyDetailsForm.valid) {
      this.savePropertyDetails();
    } else {
      this.validateAllFormFields(this.tadPropertyDetailsForm);
    }
  }

  /**
   * Handle backward step.
   *
   * @param {any} backward
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  onBackwardClicked(backward: any) {
    this.stage_changing.emit(this.stage.backward_stage.reference);
  }

  /**
   * Prepare the form and reset the form array.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  prepareForm() {
    this.tadPropertyDetailsForm = this.fb.group({
      tadAddress: this.tadAddress,
      tadPropertyNumberBedrooms: this.tadPropertyNumberBedrooms,
      tadPropertyAuctionHouse: this.tadPropertyAuctionHouse,
      tadPropertyAuctionDay: this.tadPropertyAuctionDay,
      tadPropertyAuctionMonth: this.tadPropertyAuctionMonth,
      tadPropertyAuctionYear: this.tadPropertyAuctionYear,
      tadPropertyType: this.tadPropertyType,
      hasSearched: this.hasSearched,
      addresslookup: this.addresslookup,
    }, {
      validator: this.validateFields(
        'tadPropertyAuctionDay',
        'tadPropertyAuctionMonth',
        'tadPropertyAuctionYear',
      ),
    });
  }

  /**
   * Validate all form fields on the form when submitted.
   *
   * @param {FormGroup} formGroup
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  validateAllFormFields(formGroup: UntypedFormGroup) {
    const self = this;       // {1}
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  /**
   * Rebuilds the form for if the case is a commercial property.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  rebuildFormForCommercialProperty() {
    // Get the control for the number of bedrooms
    const bedroomsControl = this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms');
    const propertyTypeControl = this.tadPropertyDetailsForm.get('tadPropertyType');

    if (_.isNil(bedroomsControl) || _.isNil(propertyTypeControl)) {
      return;
    }

    // Create the array for validators
    let validators = [];
    let validatorsType = [];

    // If it's commercial, we set the min as 0 and remove the required
    if (this.isCommercialCase) {
      validators = [];
      validatorsType = [];
    } else {
      validators = [
        Validators.min(1),
        Validators.max(10),
        Validators.required,
      ];
      validatorsType = [
        Validators.required,
        this.propertyTypeValidator(),
      ];
    }

    // Get rid of any validators to prevent merging.
    bedroomsControl.clearValidators();
    propertyTypeControl.clearValidators();

    // Set the validators on the control.
    bedroomsControl.setValidators(validators);
    propertyTypeControl.setValidators(validatorsType);

    // Set the property's bedrooms
    this.tadPropertyNumberBedrooms = bedroomsControl as UntypedFormControl;
    this.tadPropertyType = propertyTypeControl as UntypedFormControl;

    // Remove the control
    this.tadPropertyDetailsForm.setControl(
      'tadPropertyNumberBedrooms',
      this.tadPropertyNumberBedrooms,
    );
    this.tadPropertyDetailsForm.setControl(
      'tadPropertyType',
      this.tadPropertyType,
    );

    // Update the tad property details form.
    this.tadPropertyDetailsForm.get('tadPropertyNumberBedrooms').updateValueAndValidity();
    this.tadPropertyDetailsForm.get('tadPropertyType').updateValueAndValidity();
  }

  /**
   * Subscribe to form changes.
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  subscribeToFormChanges() {

    this.tadPropertyDetailsForm.statusChanges.subscribe((change) => {

      // Save some cycles if it's not valid.
      if (change === 'INVALID') {
        return;
      }

      // Bind data to API.
      this.bindAPIData();
    });
  }

  /**
   *
   *
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  ngAfterViewChecked() {
    if (this.isSubmitted) {
      const element = this.elementRef.nativeElement.querySelector('.is-invalid');
      if (!_.isNil(element)) {
        element.scrollIntoView();
        this.isSubmitted = false;
      }
    }
  }

  /**
   * Creates an instance of TogetherAuctionDIPPropertyDetailsComponent.
   * @param {NodeAPIService} api
   * @param {FormBuilder} fb
   * @param {NodeAPIEntityService} entity
   * @param {DipStateService} state
   * @param {ChangeDetectorRef} cdr
   * @param {NgZone} zone
   * @memberof TogetherAuctionDIPPropertyDetailsComponent
   */
  constructor(private api: NodeAPIService,
    private fb: UntypedFormBuilder,
    private entity: NodeAPIEntityService,
    private elementRef: ElementRef,
    private state: DipStateService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private exception: ExceptionService,
    private router: Router) {

    // Creates the formgroup.
    this.prepareForm();

    // Setters for dip journey and property type.
    this.isDipJourney = this.state.isDipJourney.getValue();
    this.propertyType = this.state.propertyType.getValue();

    // Subscribe to form changes.
    this.subscribeToFormChanges();

    // Rebuild the form if it's a commercial property.
    this.rebuildFormForCommercialProperty();
  }
}
